import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

const reportsFormSchema = z.object({
  show_boundary: z.boolean(),
  show_job_details_in_export: z.boolean(),
  export_all_rows_in_report: z.boolean(),
  interpret_ship_to_be_stopped_below_speed: z
    .union([z.number(), z.literal('')])
    .optional(),
  enable_standard_vessel_filter: z.boolean(),
  create_report_default_template: z.enum(['wizard', 'quick']),
});

export type ReportsFormValues = z.infer<typeof reportsFormSchema>;

export const useReportsForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mutate: mutateSettings } = useSaveSettings();
  const { setUnsavedSettings } = useSettingsPage();

  const initialValues = useMemo(
    () => ({
      show_boundary: settings['ribbonPlugin.settings'].report_show_boundary,
      show_job_details_in_export:
        settings['ribbonPlugin.settings'].report_show_job_details,
      export_all_rows_in_report:
        settings['ribbonPlugin.settings'].report_export_all,
      interpret_ship_to_be_stopped_below_speed:
        settings['ribbonPlugin.settings'].report_ship_stopped_speed ?? '',
      enable_standard_vessel_filter:
        settings['ribbonPlugin.settings'].report_exclude_all_non_vessels,
      create_report_default_template:
        settings['ribbonPlugin.settings'].report_default_template ?? 'wizard',
    }),
    [settings]
  );

  const form = useForm<ReportsFormValues>({
    resolver: zodResolver(reportsFormSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    form.reset(initialValues);
  }, [form, initialValues]);

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  const onSubmit = (data: ReportsFormValues) => {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        report_show_boundary: data.show_boundary,
        report_show_job_details: data.show_job_details_in_export,
        report_export_all: data.export_all_rows_in_report,
        report_ship_stopped_speed:
          data.interpret_ship_to_be_stopped_below_speed === ''
            ? undefined
            : data.interpret_ship_to_be_stopped_below_speed,
        report_exclude_all_non_vessels: data.enable_standard_vessel_filter,
        report_default_template: data.create_report_default_template,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-reports-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsReportSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-reports-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsReportFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  return {
    form,
    onSubmit,
    isSubmitting,
  };
};
